<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmDeleteProductTemplateDialog"
            :data="currentProductTemplateId"
            :action-loading="deleteLoading"
            removal
            @on-confirm="deleteProductTemplate"
            @on-abort="closeConfirmDeleteProductTemplateDialog"
        >
            <template #title>
                Usuwanie szablonu produktu
            </template>
            <template #content>
                Czy na pewno chcesz usunąć szablon produktu?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <page-title>Szablony produktowe</page-title>

        <v-row align="center">
            <v-col cols="12" sm="6" lg="4">
                <v-btn
                    color="secondary"
                    data-sentry-label="Dodaj szablon produktu"
                    @click="createProductTemplate"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj szablon produktu
                </v-btn>
            </v-col>
            <v-col cols="12" sm="6" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" sm="8" md="6" lg="4" xl="3">
                            <v-text-field
                                v-model="filters.name"
                                append-icon="mdi-magnify"
                                label="Nazwa..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('name')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="productTemplates"
                        :loading="productTemplatesLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="productTemplatesData"
                        @pagination="updatePagination"
                    >
                        <template #item.name="{item}">
                            {{ item.name }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Kopiuj"
                                            v-on="on"
                                            @click="copyProductTemplate(item.id)"
                                        >
                                            <v-icon color="primary">
                                                mdi-content-copy
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Kopiuj</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Edytuj"
                                            v-on="on"
                                            @click="editProductTemplate(item.id)"
                                        >
                                            <v-icon color="teal">
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edytuj</span>
                                </v-tooltip>
                                <v-tooltip top :open-delay="tooltipDelay">
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="showConfirmDeleteProductTemplateDialog(item.id)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'ProductTemplates',
    components: {
        PageTitle, ConfirmDialog
    },
    mixins: [
        filters,
        pagination,
        tooltipDelay,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('orders/clearProductTemplatesFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            currentProductTemplateId: null,
            saveProductTemplateDialog: false,
            deleteProductTemplateDialog: false,
            confirmDeleteProductTemplateDialog: false,
            actionLoading: false,
            deleteLoading: false,
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        productTemplates() {
            return this.$store.state.orders.productTemplates.productTemplates
        },
        productTemplatesLoading() {
            return this.$store.state.orders.productTemplates.productTemplatesLoading
        },
        savedFilters() {
            return this.$store.state.orders.productTemplates.filters
        },
        selectedProductTemplateId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    mounted() {
        this.getProductTemplates()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedProductTemplateId) {
                return 'isSelected'
            }
        },
        getProductTemplates() {
            this.$store.dispatch('orders/getProductTemplates', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createProductTemplate() {
            this.actionLoading = true
            router.push({ name: 'ordersProductTemplatesProductTemplateCreate' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        copyProductTemplate(id) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(id)
            router.push({
                name: 'ordersProductTemplatesProductTemplateCreate',
                query: { copyId: id }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        editProductTemplate(id) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(id)
            router.push({ name: 'ordersProductTemplatesProductTemplateEdit', params: { id: id } })
                .catch(error => {
                    errorHandler(error)
                })
        },
        deleteProductTemplate(id) {
            this.deleteLoading = true
            this.$store.dispatch('orders/deleteProductTemplate', id)
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.deleteLoading = false
                    this.closeConfirmDeleteProductTemplateDialog()
                })
        },
        showConfirmDeleteProductTemplateDialog(id) {
            this.currentProductTemplateId = id
            this.confirmDeleteProductTemplateDialog = true
        },
        closeConfirmDeleteProductTemplateDialog() {
            this.confirmDeleteProductTemplateDialog = false
            this.currentProductTemplateId = null
        },
        changeFilter() {
            this.$store.dispatch('orders/setProductTemplatesFilters', this.filters)
            this.resetPagination()
            this.getProductTemplates()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getProductTemplates()
            }
            this.clearPaginationPending()
        }
    }
}
</script>
